import axios from 'axios';

// Create axios instance with base configuration
// API Base URL Configuration:
// 
// 1. Environment Variable (Recommended for production):
//    Set REACT_APP_API_URL in .env or .env.production
//    Example: REACT_APP_API_URL=http://192.168.2.64:5000/api
//    Example: REACT_APP_API_URL=https://yourdomain.com/api
//
// 2. Auto-detection (Development fallback):
//    - If accessed via localhost → http://localhost:5000/api
//    - If accessed via LAN IP (e.g., 192.168.2.64:3000) → http://192.168.2.64:5000/api
//    - If accessed via domain → uses same hostname with port 5000
//
// IMPORTANT FOR PRODUCTION:
// If your reverse proxy (Nginx/Apache) adds /api to requests:
//   Set REACT_APP_API_URL WITHOUT /api (e.g., https://yourdomain.com)
//   The reverse proxy will add /api, making final URL: https://yourdomain.com/api/...
//
// If your backend serves /api directly (no reverse proxy):
//   Set REACT_APP_API_URL WITH /api (e.g., https://yourdomain.com/api)
const getBaseURL = () => {
  // Priority 1: Use environment variable if set
  const envURL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL;
  
  if (envURL) {
    // Remove trailing slash
    let baseURL = envURL.replace(/\/$/, '');
    
    // Ensure /api is included for localhost/127.0.0.1
    if (baseURL.includes('localhost') || baseURL.includes('127.0.0.1')) {
      if (!baseURL.endsWith('/api')) {
        return `${baseURL}/api`;
      }
      return baseURL;
    }
    
    // For production: Use as-is
    // If reverse proxy adds /api, set this WITHOUT /api
    // If backend serves /api directly, set this WITH /api
    return baseURL;
  }
  
  // Priority 2: Auto-detect based on current hostname
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol === 'https:' ? 'https' : 'http';
  
  // Check if it's a network IP address (e.g., 192.168.2.64)
  const isIPAddress = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(currentHost);
  const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
  
  if (isIPAddress && !isLocalhost) {
    // Network IP detected (e.g., 192.168.2.64:3000) → use same IP for backend
    return `${currentProtocol}://${currentHost}:5000/api`;
  }
  
  if (isLocalhost) {
    // Default for localhost
    return 'http://localhost:5000/api';
  }
  
  // For domain names or other cases, use same hostname with backend port
  return `${currentProtocol}://${currentHost}:5000/api`;
};

const baseURLValue = getBaseURL();

// Only log in development
if (process.env.NODE_ENV === 'development') {
  console.log('[DEBUG] Axios baseURL:', baseURLValue);
}

const api = axios.create({
  baseURL: baseURLValue,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor - Add token to requests
api.interceptors.request.use(
  (config) => {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      const fullURL = config.baseURL + config.url;
      console.log('[DEBUG] Request URL:', { baseURL: config.baseURL, url: config.url, fullURL, method: config.method });
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only log errors in development
    if (process.env.NODE_ENV === 'development') {
      if (error.response?.status === 404) {
        console.error('[DEBUG] 404 Error:', {
          baseURL: error.config?.baseURL,
          url: error.config?.url,
          fullURL: error.config?.baseURL + error.config?.url,
          status: error.response?.status,
          statusText: error.response?.statusText
        });
      }
      if (error.code === 'ECONNABORTED' || error.message === 'Network Error' || !error.response) {
        console.error('[DEBUG] Connection Error:', {
          code: error.code,
          message: error.message,
          baseURL: error.config?.baseURL,
          url: error.config?.url,
          fullURL: error.config?.baseURL + error.config?.url,
          currentHost: window.location.hostname,
          currentPort: window.location.port
        });
      }
    }
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
      console.error('Request timeout or network error:', error.message);
      // Don't redirect on timeout, let the component handle it
    }
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

